import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { secureCall } from '../../services/secureApiClient';
import { logWarn } from '../../utils/logger';

interface CheckoutResult {
  success: boolean;
  checkoutUrl?: string;
  error?: string;
}

interface SubscriptionPlansProps {
  onClose?: () => void;
  className?: string;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  onClose,
  className = ''
}) => {
  const [billingCycle, setBillingCycle] = useState<'yearly' | 'monthly'>('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();
  const { showError } = useToast();

  const handleUpgrade = async () => {
    if (!currentUser) {
      showError('Please sign in first');
      return;
    }

    try {
      setIsLoading(true);

      // 🎯 使用 SecureApiClient 透明處理所有認證問題
      // 年度方案會自動套用折扣碼（後端處理）
      const data = await secureCall<CheckoutResult>('createSubscriptionCheckout', { billingCycle });

      if (data.success && data.checkoutUrl) {
        // 導向到 Polar.sh 結帳頁面
        window.location.href = data.checkoutUrl;
      } else {
        logWarn('SubscriptionPlans', '結帳會話創建失敗', { error: data.error });
        showError('Failed to create checkout session, please try again later');
      }
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };
      logWarn('SubscriptionPlans', '付款升級失敗', {
        error: error instanceof Error ? error.message : 'Unknown error'
      });

      // 簡化錯誤處理（認證問題由 SecureApiClient 透明處理）
      let errorMessage = 'Upgrade failed, please try again later';

      if (firebaseError?.code === 'failed-precondition') {
        errorMessage = 'You are already a Pro member!';
      } else if (firebaseError?.message) {
        errorMessage = firebaseError.message;
      }

      showError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // 檢查是否在年末促銷期間（2026/01/01 前）
  const isYearEndPromoActive = () => {
    const now = new Date();
    const promoEndDate = new Date('2026-01-01T00:00:00.000Z');
    return now < promoEndDate;
  };

  // 計算價格（考慮促銷期）
  const isPromoActive = isYearEndPromoActive();
  const isYearly = billingCycle === 'yearly';

  // 基礎價格設定
  const monthlyPrice = 12;
  const yearlyStandardPrice = 96;
  const yearlyPromoPrice = 67.20; // 30% OFF

  // 計算顯示價格（統一為月均價）
  let displayPrice = monthlyPrice;  // 月繳：$12
  let originalPriceDisplay: number | null = null;

  if (isYearly) {
    // 年繳：顯示月均價
    if (isPromoActive) {
      displayPrice = yearlyPromoPrice / 12;           // 67.20 / 12 = 5.60
      originalPriceDisplay = yearlyStandardPrice / 12; // 96 / 12 = 8.00
    } else {
      displayPrice = yearlyStandardPrice / 12;        // 96 / 12 = 8.00
    }
  }

  // 計費週期說明文字
  const getBillingNote = (): string => {
    if (billingCycle === 'monthly') {
      return 'Billed monthly, cancel anytime';
    }
    return 'Billed annually';
  };

  return (
    <div className={`subscription-plans ${className} min-h-full flex flex-col items-center justify-center p-4 md:p-8 relative overflow-hidden bg-gray-50/50`}>

      {/* 背景裝飾 (Ambient Glow) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-4xl opacity-50 pointer-events-none z-0">
        <div className="absolute top-10 left-10 w-72 h-72 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-0 right-10 w-72 h-72 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-72 h-72 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      {/* 標題區 - 置中佈局 */}
      <div className="w-full max-w-xl text-center mb-6 md:mb-8 relative z-10">
        {/* 主標題 */}
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-2">
          Orlo Pro
        </h2>

        {/* 副標題 */}
        <p className="text-gray-600 text-base md:text-lg mb-4">
          Your Life Companion, Without Limits
        </p>

        {/* 計費週期切換鈕 */}
        <div className="flex items-center justify-center gap-3 text-sm">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`font-medium transition-all duration-200 ${
              !isYearly
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            Monthly
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`font-medium transition-all duration-200 ${
              isYearly
                ? 'text-gray-900 border-b-2 border-gray-900'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            Yearly
          </button>
        </div>
      </div>

      {/* 核心卡片容器 - 專注於升級 */}
      <div className="w-full max-w-md bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-gray-200 relative z-10 transition-all duration-300">

        <div className="pt-6 pb-8 px-8 md:pt-8 md:pb-12 md:px-12">

          {/* Pricing Display */}
          <div className="text-center mb-12 relative">

            <div className="flex items-center justify-center gap-3 items-baseline mb-2">
              <span className="text-2xl text-gray-400 font-medium">$</span>

              {isYearly && originalPriceDisplay && (
                <span className="relative text-3xl text-gray-300 mr-1">
                  {originalPriceDisplay}
                  <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="w-[220%] h-0.5 bg-pink-400 transform -rotate-[40deg]"></span>
                  </span>
                </span>
              )}

              <span className="text-6xl font-bold text-gray-900 tracking-tight">
                {displayPrice.toFixed(2)}
              </span>
              <span className="text-gray-500 font-medium text-lg">/month</span>
            </div>

            {/* 計費週期說明 */}
            <div className="text-sm text-gray-500 mt-3 flex items-center justify-center gap-2">
              <span>{getBillingNote()}</span>
              {isYearly && isPromoActive && (
                <span className="inline-flex items-center bg-sky-50 text-sky-600 text-xs font-medium px-2 py-0.5 rounded-full border border-sky-200">
                  30% OFF
                </span>
              )}
            </div>
          </div>

          {/* Feature List */}
          <div className="grid gap-y-5 mb-12 px-2 md:px-4">
            <FeatureItem text="Continuously Optimized Memory" />
            <FeatureItem text="Unlimited Tasks & Captures" />
            <FeatureItem text="Unlimited Goals & Generations" />
            <FeatureItem text="Deep Coach Insights" />
            <FeatureItem text="Permanent History" />
            <FeatureItem text="Priority Support" />
          </div>

          {/* Actions */}
          <div className="space-y-4 max-w-md mx-auto">
            <button
              onClick={handleUpgrade}
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Processing...' : 'Upgrade to Pro'}
            </button>

            {onClose && (
              <button
                onClick={onClose}
                className="w-full py-2 text-gray-400 hover:text-gray-600 transition-colors text-sm"
              >
                No thanks, I'll stick with Free
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Trust Badges */}
      <div className="mt-8 flex items-center justify-center gap-8 text-gray-400 text-xs font-medium z-10">
        <span className="flex items-center gap-1.5 hover:text-gray-500 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
          Secure Payment
        </span>
        <span className="flex items-center gap-1.5 hover:text-gray-500 transition-colors">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Cancel Anytime
        </span>
      </div>
    </div>
  );
};

// Helper Component for Feature Items
const FeatureItem: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-start gap-3">
    <div className="flex-shrink-0 mt-0.5">
      <svg className="w-5 h-5 text-sky-500" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    </div>
    <span className="text-gray-700 font-medium">{text}</span>
  </div>
);

export default SubscriptionPlans;